import { appState, dispatch } from "../redux/redux-config";
import { pushNotificationApi } from "../api/push-notification-api";
import {
    isPushSupported,
    requestNotificationPermission,
    subscribeUserToPush,
    unsubscribeUserFromPush,
    getExistingSubscription
} from "../utilities/push-notifications";
import { showToast } from "../components/toast";

const pushNotifications = {
    /**
     * Requests permission, creates a push subscription, and saves it to the backend.
     * Returns true on success, false on denial/unsupported.
     */
    enable: async () => {
        if (!isPushSupported()) {
            showToast("error", "Push notifications are not supported in this browser.");
            return false;
        }

        const permission = await requestNotificationPermission();
        if (permission !== "granted") {
            dispatch("PUSH_NOTIFICATIONS_ENABLED", false);
            return false;
        }

        const token = appState().user.token;
        const vapidResult = await pushNotificationApi.getVapidKey(token);
        if (!vapidResult?.success) return false;

        const vapidPublicKey = vapidResult.vapidPublicKey;
        if (!vapidPublicKey) {
            showToast("error", "Push notifications are not configured. Please contact your administrator.");
            return false;
        }
        
        let subscription;
        try {
            subscription = await subscribeUserToPush(vapidPublicKey);
        } catch (err) {
            console.error("[PushNotifications] subscribeUserToPush failed:", err);
            const isSSLError = err?.message?.includes('SSL') || err?.message?.includes('certificate') || err?.name === 'SecurityError';
            const isSwTimeout = err?.message?.includes('Service worker did not activate');
            const message = isSSLError
                ? "Push notifications require a trusted SSL certificate. In development, enable chrome://flags/#allow-insecure-localhost."
                : isSwTimeout
                ? "Push notifications setup timed out. Please refresh the page and try again."
                : "Failed to enable push notifications. Please check your browser settings.";
            showToast("error", message);
            return false;
        }

        const result = await pushNotificationApi.saveSubscription(token, subscription);
        
        if (result?.success) {
            dispatch("PUSH_NOTIFICATIONS_ENABLED", true);
            return true;
        }
        return false;
    },

    /**
     * Unsubscribes from push in the browser and removes the subscription from the backend.
     */
    disable: async () => {
        const token = appState().user.token;
        await unsubscribeUserFromPush();
        await pushNotificationApi.deleteSubscription(token);
        dispatch("PUSH_NOTIFICATIONS_ENABLED", false);
    },

    /**
     * Checks the browser for an existing subscription and syncs Redux state.
     */
    checkStatus: async () => {
        const existing = await getExistingSubscription();
        const isEnabled = existing !== null && Notification.permission === "granted";
        dispatch("PUSH_NOTIFICATIONS_ENABLED", isEnabled);
        return isEnabled;
    }
};

export default pushNotifications;

export function pushNotificationsReducer(state = { enabled: false }, action) {
    switch (action.type) {
        case "PUSH_NOTIFICATIONS_ENABLED":
            return { ...state, enabled: action.payload };
        default:
            return state;
    }
}
